const resolvedDefaultApiBase =
  typeof window !== 'undefined' && window.location.hostname === 'localhost'
    ? 'http://localhost:3001/api/tdash'
    : '/api/tdash';

const configuredBase = import.meta.env.VITE_API_URL ? import.meta.env.VITE_API_URL.replace(/\/$/, '') : '';
const API_BASE = configuredBase
  ? (configuredBase.endsWith('/tdash') ? configuredBase : `${configuredBase}/tdash`)
  : resolvedDefaultApiBase;

function getAuthHeaders(): Record<string, string> {
  const token = typeof window !== 'undefined' ? localStorage.getItem('scrudix_token') : null;
  const dsKey = typeof window !== 'undefined' ? (localStorage.getItem('scrudix_deepseek_api_key') || localStorage.getItem('deepseek_api_key') || '') : '';
  const dsModel = typeof window !== 'undefined' ? (localStorage.getItem('scrudix_deepseek_model') || 'deepseek-chat') : 'deepseek-chat';
  const headers: Record<string, string> = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (dsKey) {
    headers['x-deepseek-api-key'] = dsKey;
    headers['x-deepseek-model'] = dsModel;
  }
  return headers;
}

export async function apiGet<T = unknown>(path: string): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'GET',
    credentials: 'include',
    headers: { ...getAuthHeaders() },
  });
  if (!res.ok) throw new Error(`GET ${path} failed: ${res.status}`);
  return res.json();
}

export async function apiPost<T = unknown>(path: string, body: unknown): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`POST ${path} failed: ${res.status}`);
  return res.json();
}

export async function apiPut<T = unknown>(path: string, body: unknown): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`PUT ${path} failed: ${res.status}`);
  return res.json();
}

export async function apiPatch<T = unknown>(path: string, body: unknown): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`PATCH ${path} failed: ${res.status}`);
  return res.json();
}

export async function apiDelete<T = unknown>(path: string): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'DELETE',
    credentials: 'include',
    headers: { ...getAuthHeaders() },
  });
  if (!res.ok) throw new Error(`DELETE ${path} failed: ${res.status}`);
  return res.json();
}

/* ─────────────────────────────────────────────────────────────────────────────
   TEAM INTELLIGENCE CLIENT API
───────────────────────────────────────────────────────────────────────────── */

import type {
  TeamPulseMetrics,
  ActionCenterAlert,
  ProjectRadarItem,
  DeliveryIntelligenceData,
  BudgetForecastData,
  TeamDecisionRecord,
  SimulationScenario,
  SimulationResult,
} from './types';

export async function getTeamPulse(slug: string): Promise<TeamPulseMetrics> {
  const res = await apiGet<{ success: boolean; data: TeamPulseMetrics }>(`/${slug}/intelligence/pulse`);
  return res.data;
}

export async function getActionAlerts(slug: string): Promise<ActionCenterAlert[]> {
  const res = await apiGet<{ success: boolean; data: ActionCenterAlert[] }>(`/${slug}/intelligence/alerts`);
  return res.data;
}

export async function updateAlertStatus(
  slug: string,
  alertId: string,
  status: 'resolved' | 'snoozed' | 'dismissed'
): Promise<ActionCenterAlert> {
  const res = await apiPatch<{ success: boolean; data: ActionCenterAlert }>(
    `/${slug}/intelligence/alerts/${alertId}`,
    { status }
  );
  return res.data;
}

export async function getProjectRadar(slug: string): Promise<ProjectRadarItem[]> {
  const res = await apiGet<{ success: boolean; data: ProjectRadarItem[] }>(`/${slug}/intelligence/projects`);
  return res.data;
}

export async function getDeliveryIntelligence(slug: string): Promise<DeliveryIntelligenceData> {
  const res = await apiGet<{ success: boolean; data: DeliveryIntelligenceData }>(`/${slug}/intelligence/delivery`);
  return res.data;
}

export async function getBudgetForecast(slug: string): Promise<BudgetForecastData> {
  const res = await apiGet<{ success: boolean; data: BudgetForecastData }>(`/${slug}/intelligence/budget-forecast`);
  return res.data;
}

export async function getTeamDecisions(slug: string): Promise<TeamDecisionRecord[]> {
  const res = await apiGet<{ success: boolean; data: TeamDecisionRecord[] }>(`/${slug}/intelligence/decisions`);
  return res.data;
}

export async function createTeamDecision(
  slug: string,
  decision: Partial<TeamDecisionRecord>
): Promise<TeamDecisionRecord> {
  const res = await apiPost<{ success: boolean; data: TeamDecisionRecord }>(`/${slug}/intelligence/decisions`, decision);
  return res.data;
}

export async function runSimulation(slug: string, scenario: SimulationScenario): Promise<SimulationResult> {
  const res = await apiPost<{ success: boolean; data: SimulationResult }>(`/${slug}/intelligence/simulate`, scenario);
  return res.data;
}

export async function getDailyStandup(
  slug: string
): Promise<{ content: string; lastUpdated: string; updatedBy: string }> {
  const res = await apiGet<{ success: boolean; data: { content: string; lastUpdated: string; updatedBy: string } }>(
    `/${slug}/intelligence/standup`
  );
  return res.data;
}

export async function saveDailyStandup(
  slug: string,
  content: string,
  updatedBy?: string
): Promise<{ content: string; lastUpdated: string; updatedBy: string }> {
  const res = await apiPost<{ success: boolean; data: { content: string; lastUpdated: string; updatedBy: string } }>(
    `/${slug}/intelligence/standup`,
    { content, updatedBy }
  );
  return res.data;
}

export async function askTeamLumi(
  slug: string,
  message: string
): Promise<{ reply: string; bullets?: string[]; recommendedSteps?: string[]; timestamp: string }> {
  const res = await apiPost<{ success: boolean; data: { reply: string; bullets?: string[]; recommendedSteps?: string[]; timestamp: string } }>(
    `/${slug}/intelligence/chat`,
    { message }
  );
  return res.data;
}

